import {
  CRON_MONITORING_JOBS,
  CronJobLogDetail,
  CronJobLogListItem,
  CronMonitoringJobDefinition,
} from "../models/admin-monitoring.models";

export const UNKNOWN_CRON_JOB: CronMonitoringJobDefinition = {
  name: "UNKNOWN",
  title: "Unknown cron job",
  group: "Other",
  focus: "No local dashboard definition exists for this cron job name yet.",
  metricKeys: [],
};

const metricLabels: Record<string, string> = {
  allAccountsNo: "Accounts",
  attemptedCount: "Attempted",
  activatedVesselsNo: "Activated",
  balticFormsNo: "BQ reminders",
  bqCandidatesNo: "BQ candidates",
  bqsToCreateNo: "BQs to create",
  candidateCount: "Candidates",
  chasersDue: "Chasers due",
  completedVessels: "Completed vessels",
  createdBQsNo: "Created BQs",
  createdCount: "Created",
  createdRequestsNo: "Created requests",
  createdTFsNo: "Created TFs",
  docsNo: "Documents",
  duplicateCount: "Duplicates",
  failedCount: "Failed",
  inactiveVesselsNo: "Inactive",
  initialRequestsDue: "Initial requests due",
  initialRequestsAttempted: "Initial requests attempted",
  inserted: "Inserted",
  importsCount: "Imports",
  loadedDryDockRowsNo: "Dry dock rows",
  loadedHullEventRowsNo: "Hull event rows",
  mailboxCandidateEmails: "Mailbox candidates",
  masterFeedbackEnabled: "Master corrections enabled",
  masterFeedbackEligibleAttachments: "Correction attachments",
  masterFeedbackRecipients: "Correction recipients",
  masterFeedbackPendingBatchesFound: "Pending correction batches",
  masterFeedbackPendingBatchesDeferred: "Deferred correction batches",
  masterFeedbackAttempts: "Correction emails attempted",
  masterFeedbackDeliveryConfirmed: "Correction emails confirmed",
  masterFeedbackDeliveryUnconfirmed: "Correction emails unconfirmed",
  matchedRequestRowsNo: "Matched rows",
  manualRequestsToAdopt: "Manual requests to adopt",
  missingEmails: "Missing emails",
  missingEmailsNo: "Missing emails",
  nameChangeCandidatesNo: "Name changes",
  notifsNo: "Notifications",
  onboardedVesselsNo: "Onboarded",
  onboardingCandidatesNo: "Candidates",
  notDueVessels: "Not due",
  periodVesselsProcessed: "Period vessels",
  longTenureCandidateVessels: "Long-tenure vessels",
  longTenureCrewMembersDiscovered: "Long-tenure crew",
  longTenureCampaignsToStart: "Refresh campaigns due",
  longTenureCampaignsStarted: "Refresh campaigns started",
  longTenureManualRequestsToAdopt: "Refresh manual requests to adopt",
  longTenureManualRequestsAdopted: "Refresh manual requests adopted",
  longTenureInitialRequestsDue: "Refresh requests due",
  longTenureInitialRequestsAttempted: "Refresh requests attempted",
  longTenureChasersDue: "Refresh chasers due",
  longTenureChasersAttempted: "Refresh chasers attempted",
  longTenureMissingEmails: "Refresh missing emails",
  longTenureInvalidEmails: "Refresh invalid emails",
  longTenureResponsesImported: "Refresh responses",
  longTenureStillOverdueEscalationsDue: "Still-serving escalations due",
  longTenureStillOverdueEscalationsCreated: "Still-serving escalations",
  longTenureNoResponseEscalationsDue: "No-response escalations due",
  longTenureNoResponseEscalationsCreated: "No-response escalations",
  longTenureNotificationDeliveryUnconfirmed: "Unconfirmed refresh notifications",
  longTenureErrors: "Long-tenure errors",
  processedRequestsNo: "Outbox rows",
  previewErrors: "Preview errors",
  provisionedNo: "Provisioned",
  requestRowsInsertedNo: "Rows inserted",
  retriedIncompleteRequestsNo: "Retried incomplete",
  sentCount: "Sent",
  sentEmailsNo: "Sent emails",
  skippedCount: "Skipped",
  skippedAlreadyRequestedNo: "Already requested",
  skippedDuplicateCandidatesNo: "Duplicate candidates",
  skippedEmailsNo: "Skipped emails",
  success: "Success",
  techFormsNo: "TF reminders",
  tfCandidatesNo: "TF candidates",
  tfsToCreateNo: "TFs to create",
  tfsToResetNo: "TFs to reset",
  vesselsToOnboardNo: "To onboard",
  vesselsToRequestNo: "To request",
};

export const getCronJobDefinition = (name: string): CronMonitoringJobDefinition =>
  CRON_MONITORING_JOBS.find((job) => job.name === name) ?? {
    ...UNKNOWN_CRON_JOB,
    name,
    title: titleCaseFromCronName(name),
  };

export const formatCronDateTime = (value: string | null | undefined): string => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

export const formatCronTime = (value: string | null | undefined): string => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const formatCronDay = (value: string): string => {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

export const formatMetricLabel = (key: string): string =>
  metricLabels[key] ?? key.replace(/([A-Z])/g, " $1").replace(/^./, (letter) => letter.toUpperCase());

export const formatMetricValue = (value: unknown): string => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "number") {
    return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
  }

  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  return String(value);
};

export const getCronLogMetrics = (log: CronJobLogListItem | CronJobLogDetail | null | undefined) => {
  if (!log) {
    return [];
  }

  const definition = getCronJobDefinition(log.name);
  const keys = [
    ...definition.metricKeys,
    ...Object.keys(log.metrics).filter((key) => !definition.metricKeys.includes(key)),
  ];

  return keys
    .filter((key) => log.metrics[key] !== undefined)
    .slice(0, definition.metricLimit ?? 6)
    .map((key) => ({
      key,
      label: formatMetricLabel(key),
      value: formatMetricValue(log.metrics[key]),
      tone: getMetricTone(key, log.metrics[key]),
    }));
};

export const formatCronJson = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  if (typeof value === "string") {
    return value;
  }

  return JSON.stringify(value, null, 2);
};

function titleCaseFromCronName(name: string): string {
  return name
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getMetricTone(key: string, value: string | number | boolean): "success" | "warning" | "danger" | "neutral" {
  const lowerKey = key.toLowerCase();
  const numeric = typeof value === "number" ? value : Number.NaN;

  if (lowerKey.includes("failed") || lowerKey.includes("error")) {
    return numeric > 0 ? "danger" : "success";
  }

  if (
    lowerKey.includes("missing") ||
    lowerKey.includes("skipped") ||
    lowerKey.includes("duplicate") ||
    lowerKey.includes("unconfirmed")
  ) {
    return numeric > 0 ? "warning" : "success";
  }

  if (lowerKey.includes("sent") || lowerKey.includes("created") || lowerKey.includes("activated")) {
    return numeric > 0 ? "success" : "neutral";
  }

  return "neutral";
}
